// ============================================================================
// MÓDULO S1: PETITION ENGINE (VERSÃO 1.0 — GUIDED PETITION INTAKE)
// ARQUIVO: src/features/petitions/PetitionEngine.jsx
// OBJETIVO: Motor nativo para redação de Petições Judiciais (Art. 319 CPC)
//           Arquitetura CDAS · Padrão SAGA · SHA-256 · Molde A4
// ============================================================================
import React, { useCallback, useMemo, useState, useRef } from "react";
import { FIREBASE_CONFIG } from "../../config/Config";
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import { db, auth, storage } from "../../shared/api/firebase/config";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
  deleteObject,
} from "firebase/storage";
import { computeFileSHA256 } from "../../utils/cryptoUtils";
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import { CadernoSelector } from "../../components/ui/CadernoSelector";
import {
  DocumentHeader,
  DocumentFooter,
} from "../../components/layout/DocumentLetterhead";
import {
  DocumentShell,
  ConfigSection,
} from "../../components/layout/DocumentShell";

import {
  FileText,
  User,
  Users,
  Scale,
  Gavel,
  List,
  DollarSign,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  Loader2,
  Eye,
  Edit3,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  BookOpen,
} from "lucide-react";

import "../../styles/legal-print.css";
import { fetchWithWarmup } from "../../utils/backendWarmup";
import { generateQualificationText } from "../../config/ClientSchema";
import { InformedConsentFactory } from "./ConsentBuilder";

const appId = FIREBASE_CONFIG.projectId;
const PUBLISHING_API_BASE_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || "https://rda-backend-thuq.onrender.com",
)
  .trim()
  .replace(/\/+$/, "");

// --- DADOS DO ADVOGADO ---
const OFFICE_LAWYER = {
  name: "Rodrigo Demeterco",
  oab: "OAB/PR 113.938",
  address: "Rua Bom Jesus de Iguape, 2035, Hauer, Curitiba/PR, CEP 81.610-040",
  email: "rodrigodemeterco@hotmail.com",
  phone: "(41) 99848-1711",
};

// --- SEÇÕES DO FORMULÁRIO (Art. 319 CPC) ---
const FORM_SECTIONS = [
  {
    id: "setup",
    label: "Setup",
    icon: FileText,
    desc: "Cliente e Caderno de Autos",
  },
  {
    id: "enderecamento",
    label: "Endereçamento",
    icon: Scale,
    desc: "Juízo competente",
  },
  {
    id: "partes",
    label: "Qualificação",
    icon: Users,
    desc: "Qualificação das partes",
  },
  { id: "fatos", label: "Dos Fatos", icon: BookOpen, desc: "Narrativa fática" },
  {
    id: "fundamentos",
    label: "Do Direito",
    icon: Gavel,
    desc: "Fundamentos jurídicos",
  },
  {
    id: "pedidos",
    label: "Dos Pedidos",
    icon: List,
    desc: "Requerimentos ao Juízo",
  },
  {
    id: "complemento",
    label: "Complemento",
    icon: DollarSign,
    desc: "Valor da causa, provas, audiência",
  },
];

const DEFAULT_FORM = {
  tipoPeticao: "Ação",
  tipoAcao: "",
  juizo: "",
  comarca: "Curitiba/PR",
  qualificacaoAutor: "",
  qualificacaoReu: "",
  nomeReu: "",
  fatos: "",
  fundamentos: "",
  pedidos: [""],
  valorCausa: "",
  provas:
    "Protesta provar o alegado por todos os meios de prova em direito admitidos, especialmente prova documental, testemunhal e pericial, caso necessário.",
  audienciaConciliacao: true,
};

// ============================================================================
// COMPONENTE: PetitionEngine
// ============================================================================
const PetitionEngine = () => {
  const { user, email, getToken } = useCurrentUser();
  // --- State: Form ---
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [activeSection, setActiveSection] = useState("setup");

  // --- State: Client & Caderno ---
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [cadernoId, setCadernoId] = useState("");
  const [cadernoTitle, setCadernoTitle] = useState("");

  // --- State: UI ---
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [notice, setNotice] = useState("");
  const [showConsentBuilder, setShowConsentBuilder] = useState(false);
  const noticeTimer = useRef(null);

  const pushNotice = useCallback((msg) => {
    setNotice(msg);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(""), 8000);
  }, []);

  // --- Form Handlers ---
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClientSelect = useCallback(
    (client) => {
      setSelectedClient(client);
      setClientPickerOpen(false);
      // Usa o motor canônico de qualificação (Padrão Ouro — ClientSchema.js)
      const qualText = generateQualificationText(client);
      updateField("qualificacaoAutor", qualText);
    },
    [updateField],
  );

  const handleAddPedido = useCallback(() => {
    setForm((prev) => ({ ...prev, pedidos: [...prev.pedidos, ""] }));
  }, []);

  const handleRemovePedido = useCallback((index) => {
    setForm((prev) => ({
      ...prev,
      pedidos:
        prev.pedidos.length > 1
          ? prev.pedidos.filter((_, i) => i !== index)
          : prev.pedidos,
    }));
  }, []);

  const handleUpdatePedido = useCallback((index, value) => {
    setForm((prev) => {
      const updated = [...prev.pedidos];
      updated[index] = value;
      return { ...prev, pedidos: updated };
    });
  }, []);

  // --- Nav Helpers ---
  const currentIdx = FORM_SECTIONS.findIndex((s) => s.id === activeSection);
  const canPrev = currentIdx > 0;
  const canNext = currentIdx < FORM_SECTIONS.length - 1;

  // ============================================================================
  // PUBLICAÇÃO — PADRÃO SAGA (Intent → Upload → Commit)
  // ============================================================================
  const handlePublish = useCallback(async () => {
    if (isPublishing) return;
    const clientId = String(selectedClient?.id || "").trim();
    if (!clientId) {
      pushNotice("Selecione um cliente antes de publicar.");
      return;
    }
    if (!form.juizo.trim()) {
      pushNotice("Preencha o Juízo (endereçamento).");
      return;
    }
    if (!form.fatos.trim()) {
      pushNotice('Preencha a seção "Dos Fatos".');
      return;
    }
    if (!form.pedidos.some((p) => p.trim())) {
      pushNotice("Adicione ao menos um pedido.");
      return;
    }

    setIsPublishing(true);
    setPublishResult(null);

    let sRefForRollback = null;
    let documentRefForRollback = null;
    let uploadCompleted = false;

    try {
      if (!appId) throw new Error("ProjectId do Firebase não encontrado.");
      const actorName =
        String(user?.displayName || user?.email || "Operador").trim() ||
        "Operador";
      const clientName = String(
        selectedClient?.name ||
          selectedClient?.qualification?.physical?.name ||
          "Cliente",
      ).trim();

      // --- Token refresh ---
      if (!user) throw new Error("Sessão não iniciada. Faça login novamente.");
      try {
        await getToken(true);
      } catch {
        throw new Error("Sessão expirada ou 2FA pendente.");
      }

      // --- Build HTML ---
      pushNotice("Montando documento...");
      const title = `${form.tipoPeticao}${form.tipoAcao ? " de " + form.tipoAcao : ""} — ${clientName}`;
      const petitionHtml = buildPetitionHtml(form, clientName, title);

      // --- Generate PDF via backend ---
      pushNotice("Gerando PDF via motor Python...");
      const ts = Date.now();
      const pdfFileName = `PETICAO_${ts}_${clientName.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 40)}.pdf`;
      let pdfBlob = null;
      try {
        const resp = await fetchWithWarmup(
          `${PUBLISHING_API_BASE_URL}/drive/generate-pdf?t=${ts}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              html: petitionHtml,
              fileName: pdfFileName,
              parentId: null,
              makePublic: false,
            }),
          },
          {
            onWaking: () =>
              pushNotice?.(
                "⏳ Backend acordando... aguarde até 40s na primeira vez.",
              ),
            onRetry: (attempt, max) =>
              pushNotice?.(`🔄 Tentativa ${attempt + 1}/${max}... aguarde.`),
          },
        );
        if (!resp.ok) {
          const errText = await resp.text().catch(() => "");
          throw new Error(errText || resp.statusText || "Falha ao gerar PDF.");
        }
        pdfBlob = await resp.blob();
      } catch (fetchErr) {
        const msg = String(fetchErr?.message || "");
        if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
          throw new Error(
            "Backend de PDF (Render) fora do ar. Verifique https://dashboard.render.com.",
          );
        }
        throw fetchErr;
      }
      if (!pdfBlob || !pdfBlob.size)
        throw new Error("Motor Python retornou PDF vazio.");

      // --- SHA-256 ---
      pushNotice("Calculando hash SHA-256...");
      let authenticityHash = "";
      try {
        authenticityHash = await computeFileSHA256(pdfBlob);
      } catch (he) {
        console.warn("[RDA] SHA-256 falhou:", he);
      }

      // --- Duplicate Detection ---
      if (authenticityHash) {
        try {
          const dup = await checkDuplicatePetition(clientId, authenticityHash);
          if (dup) {
            const existing = dup.data();
            if (
              !window.confirm(
                `⚠️ Petição idêntica já publicada:\n\n"${existing?.title || "Petição anterior"}"\n\nHash SHA-256 coincide. Publicar mesmo assim?`,
              )
            ) {
              pushNotice("Publicação cancelada — duplicata detectada.");
              return;
            }
          }
        } catch (dupErr) {
          console.warn("[RDA] Falha na detecção de duplicata:", dupErr);
        }
      }

      // --- SAGA PASSO 1: INTENT ---
      pushNotice("Registrando intent no Firestore...");
      const documentRef = doc(documentsCol);
      documentRefForRollback = documentRef;
      const storagePath = `artifacts/${appId}/public/documents/petitions/${clientId}/${pdfFileName}`;

      await createPetitionDocument(
        documentRef,
        {
          id: documentRef.id,
          type: "PETITION",
          status: "UPLOADING",
          module: "petitions",
          title,
          clientId,
          relatedEntities: [clientId],
          storagePath,
          clientVisible: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: email || null,
        },
        { merge: true },
      );

      // --- SAGA PASSO 2: UPLOAD ---
      pushNotice("Enviando PDF para o Storage...");
      const sRef = storageRef(storage, storagePath);
      sRefForRollback = sRef;
      await uploadBytes(sRef, pdfBlob, { contentType: "application/pdf" });
      uploadCompleted = true;
      const downloadUrl = await getDownloadURL(sRef);

      // --- SAGA PASSO 3: COMMIT ---
      pushNotice("Finalizando publicação...");
      const commitPayload = {
        status: "pending_consent",
        name: title,
        fileName: pdfFileName,
        fileUrl: downloadUrl,
        downloadUrl,
        storageEngine: {
          provider: "firebase_storage",
          access: "authenticated_url",
          mode: "pdf_archive",
          storagePath,
        },
        mimeType: "application/pdf",
        extension: "pdf",
        category: "Petição",
        classification: { category: "Petição" },
        clientVisible: true,
        requiresConsent: true,
        consentStatus: "pending_consent",
        cadernoId: cadernoId || null,
        cadernoTitle: cadernoTitle || null,
        context: {
          module: "petitions",
          documentKind: "PETITION_PUBLICATION",
          workspaceTitle: cadernoTitle || "",
          tipoPeticao: form.tipoPeticao,
          tipoAcao: form.tipoAcao,
          juizo: form.juizo,
          comarca: form.comarca,
        },
        publication: {
          source: "petition_engine",
          publishType: "petition",
          format: "pdf",
          version: 1,
          publishedAtIso: new Date().toISOString(),
          publishedBy: actorName,
          storageMode: "pdf_archive",
        },
        htmlContent: petitionHtml,
        updatedAt: serverTimestamp(),
        updatedBy: email || null,
        ...(authenticityHash ? { authenticityHash } : {}),
      };
      await updatePetitionDocument(documentRef, commitPayload);

      // --- Prontuário Note (isolated) ---
      try {
        await addPetitionProntuarioNote(clientId, {
          category: "SYSTEM",
          type: "PETITION_PUBLISHED",
          payload: {
            text: `Petição publicada para consentimento: ${title}`,
            html: `<p><strong>Petição publicada:</strong> ${title}</p><p>Publicada por ${actorName} via Petition Engine.</p>`,
            documentId: documentRef.id,
            title,
            fileName: pdfFileName,
            fileUrl: downloadUrl,
            downloadUrl,
            storagePath,
            workspaceLabel: "Petição",
            module: "petitions",
            requiresConsent: true,
            consentStatus: "pending_consent",
            format: "pdf",
          },
          actor: { name: actorName, isBot: false },
          createdAt: serverTimestamp(),
        });
      } catch (noteErr) {
        console.warn("[RDA] Nota do prontuário falhou:", noteErr);
      }

      setPublishResult({
        success: true,
        docId: documentRef.id,
        hash: authenticityHash,
        title,
      });
      pushNotice(`✅ Petição "${title}" publicada com sucesso!`);
    } catch (err) {
      // --- SAGA ROLLBACK ---
      if (documentRefForRollback) {
        try {
          await deletePetitionDocument(documentRefForRollback);
        } catch (rb) {
          console.warn("[RDA] SAGA rollback Firestore:", rb);
        }
      }
      if (uploadCompleted && sRefForRollback) {
        try {
          await deleteObject(sRefForRollback);
        } catch (rb) {
          if (rb?.code !== "storage/object-not-found")
            console.warn("[RDA] SAGA rollback Storage:", rb);
        }
      }
      setPublishResult({
        success: false,
        error: String(err?.message || err).slice(0, 200),
      });
      pushNotice(`❌ Erro: ${String(err?.message || err).slice(0, 120)}`);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, selectedClient, form, cadernoId, cadernoTitle, pushNotice]);

  // ============================================================================
  // BUILD HTML (Petição formatada)
  // ============================================================================
  const buildPetitionHtml = useCallback((f, clientName, title) => {
    const pedidosHtml = (f.pedidos || [])
      .filter((p) => p.trim())
      .map(
        (p, i) =>
          `<p style="margin:0 0 6px 0;"><strong>${String.fromCharCode(97 + i)})</strong> ${esc(p)}</p>`,
      )
      .join("\n");

    const audienciaText = f.audienciaConciliacao
      ? "O(A) autor(a) manifesta interesse na realização de audiência de conciliação ou mediação, nos termos do art. 319, VII, do CPC."
      : "O(A) autor(a) manifesta desinteresse na realização de audiência de conciliação ou mediação, nos termos do art. 319, VII, do CPC, requerendo desde já a citação do(a) réu(ré) para apresentar contestação.";

    const dataFormatada = new Date().toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8"/>
  <title>${esc(title)}</title>
  <style>
    @page { size: A4; margin: 30mm 25mm 25mm 30mm; }
    body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.8; color: #1a1a1a; margin: 0; padding: 30mm 25mm 25mm 30mm; }
    h1 { text-align: center; font-size: 13pt; font-weight: bold; text-transform: uppercase; margin: 2em 0 1em; letter-spacing: 0.05em; }
    h2 { font-size: 12pt; font-weight: bold; text-transform: uppercase; margin: 1.5em 0 0.5em; border-bottom: 1px solid #20243B; padding-bottom: 4px; color: #20243B; }
    p { text-align: justify; margin: 0 0 0.8em; text-indent: 2em; }
    p.no-indent { text-indent: 0; }
    .enderecamento { text-align: center; font-weight: bold; font-size: 12pt; text-transform: uppercase; margin-bottom: 3em; }
    .assinatura { text-align: center; margin-top: 4em; }
    .assinatura .linha { border-top: 1px solid #333; display: inline-block; width: 60%; margin-bottom: 4px; }
    .assinatura .nome { font-weight: bold; }
    .local-data { text-align: right; margin-top: 2em; font-style: italic; }
  </style>
</head>
<body>
  <div class="enderecamento">
    EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) DE DIREITO ${esc(f.juizo ? "DA " + f.juizo.toUpperCase() : "")} DA COMARCA DE ${esc((f.comarca || "CURITIBA/PR").toUpperCase())}
  </div>

  <p>${esc(f.qualificacaoAutor || clientName)}, vem, respeitosamente, à presença de Vossa Excelência, por intermédio de seu advogado que esta subscreve (procuração em anexo — doc. 01), propor a presente</p>

  <h1>${esc(f.tipoPeticao || "AÇÃO")}${f.tipoAcao ? " DE " + esc(f.tipoAcao.toUpperCase()) : ""}</h1>

  <p class="no-indent">em face de <strong>${esc(f.nomeReu || "[RÉU NÃO INFORMADO]")}</strong>, ${esc(f.qualificacaoReu || "[qualificação não informada]")}, pelos fatos e fundamentos a seguir expostos.</p>

  <h2>I — DOS FATOS</h2>
  ${(f.fatos || "")
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => `<p>${esc(l)}</p>`)
    .join("\n")}

  <h2>II — DO DIREITO</h2>
  ${(f.fundamentos || "")
    .split("\n")
    .filter((l) => l.trim())
    .map((l) => `<p>${esc(l)}</p>`)
    .join("\n")}

  <h2>III — DOS PEDIDOS</h2>
  <p>Ante o exposto, requer:</p>
  ${pedidosHtml}
  <p>Requer, ainda, a citação do(a) réu(ré) para, querendo, contestar a presente ação no prazo legal, sob pena de revelia e confissão ficta.</p>

  ${f.valorCausa ? `<h2>IV — DO VALOR DA CAUSA</h2><p>Atribui-se à presente causa o valor de <strong>R$ ${esc(f.valorCausa)}</strong>, nos termos do art. 291 e seguintes do CPC.</p>` : ""}

  <h2>${f.valorCausa ? "V" : "IV"} — DAS PROVAS</h2>
  <p>${esc(f.provas || "Protesta provar o alegado por todos os meios de prova em direito admitidos.")}</p>

  <h2>${f.valorCausa ? "VI" : "V"} — DA AUDIÊNCIA DE CONCILIAÇÃO</h2>
  <p>${esc(audienciaText)}</p>

  <p class="local-data">${esc(f.comarca || "Curitiba/PR")}, ${dataFormatada}.</p>

  <div class="assinatura">
    <div class="linha"></div><br/>
    <div class="nome">${esc(OFFICE_LAWYER.name)}</div>
    <div>${esc(OFFICE_LAWYER.oab)}</div>
    <div style="font-size:10pt;color:#555;">${esc(OFFICE_LAWYER.email)}</div>
  </div>
</body>
</html>`;
  }, []);

  const esc = (v) =>
    String(v || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  // --- Memoized Preview HTML (for iframe) ---
  const previewHtml = useMemo(() => {
    const clientName = String(
      selectedClient?.name ||
        selectedClient?.qualification?.physical?.name ||
        "AUTOR",
    ).trim();
    const title = `${form.tipoPeticao}${form.tipoAcao ? " de " + form.tipoAcao : ""} — ${clientName}`;
    return buildPetitionHtml(form, clientName, title);
  }, [form, selectedClient, buildPetitionHtml]);

  // ============================================================================
  // RENDER — Padrão DocumentShell (3 colunas)
  // ============================================================================
  const rightPanelItems = FORM_SECTIONS.map((s, i) => ({
    id: s.id,
    label: s.label,
    isActive: activeSection === s.id,
  }));

  const actionBarJsx = (
    <>
      <button
        type="button"
        onClick={handlePublish}
        disabled={isPublishing}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-emerald-500 bg-emerald-600 text-white text-[11px] font-extrabold uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm w-full"
      >
        {isPublishing ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Send className="w-3.5 h-3.5" />
        )}
        {isPublishing ? "Publicando..." : "Finalizar e Publicar"}
      </button>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={!canPrev}
          onClick={() => setActiveSection(FORM_SECTIONS[currentIdx - 1].id)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-[10px] font-bold text-gray-500 hover:text-[#20243B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-200 rounded-lg"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Anterior
        </button>
        <button
          type="button"
          disabled={!canNext}
          onClick={() => setActiveSection(FORM_SECTIONS[currentIdx + 1].id)}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-[10px] font-bold text-gray-500 hover:text-[#20243B] disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-gray-200 rounded-lg"
        >
          Próximo <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </>
  );

  const centerPanelJsx = (
    <div className="flex-1 overflow-auto relative p-4 md:p-6 bg-gray-100 min-w-0 min-h-0 print:p-0 print:overflow-visible print:bg-white">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* SETUP */}
        {activeSection === "setup" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Cliente e Caderno"
              desc="Selecione o cliente e o caderno de autos vinculado."
            />
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Cliente (Autor)
              </label>
              {selectedClient ? (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <User className="w-4 h-4 text-blue-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-[#20243B] truncate">
                      {selectedClient.name ||
                        selectedClient.qualification?.physical?.name ||
                        "Cliente"}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {selectedClient.qualification?.physical?.cpf ||
                        selectedClient.qualification?.legal?.cnpj ||
                        ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedClient(null);
                      updateField("qualificacaoAutor", "");
                    }}
                    className="text-[10px] text-red-500 font-bold hover:text-red-700"
                  >
                    Trocar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setClientPickerOpen(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-500 font-bold hover:border-[#C5A059] hover:text-[#20243B] transition-all flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Selecionar Cliente do CRM
                </button>
              )}
            </div>
            {selectedClient && (
              <CadernoSelector
                clientId={selectedClient.id}
                value={cadernoId}
                onChange={(id, title) => {
                  setCadernoId(id);
                  setCadernoTitle(title);
                }}
                label="Vincular a Caderno de Autos"
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                  Tipo de Petição
                </label>
                <select
                  value={form.tipoPeticao}
                  onChange={(e) => updateField("tipoPeticao", e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
                >
                  <option>Ação</option>
                  <option>Petição Inicial</option>
                  <option>Petição Intermediária</option>
                  <option>Recurso de Apelação</option>
                  <option>Agravo de Instrumento</option>
                  <option>Embargos de Declaração</option>
                  <option>Contestação</option>
                  <option>Réplica</option>
                  <option>Manifestação</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                  Tipo de Ação
                </label>
                <input
                  value={form.tipoAcao}
                  onChange={(e) => updateField("tipoAcao", e.target.value)}
                  placeholder="Ex: Indenização por Danos Morais"
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* ENDEREÇAMENTO */}
        {activeSection === "enderecamento" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Endereçamento"
              desc="Juízo competente para receber a petição (Art. 319, I, CPC)."
            />
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Juízo
              </label>
              <input
                value={form.juizo}
                onChange={(e) => updateField("juizo", e.target.value)}
                placeholder="Ex: 1ª Vara Cível do Foro Central"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Comarca
              </label>
              <input
                value={form.comarca}
                onChange={(e) => updateField("comarca", e.target.value)}
                placeholder="Curitiba/PR"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
              />
            </div>
          </div>
        )}

        {/* QUALIFICAÇÃO DAS PARTES */}
        {activeSection === "partes" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Qualificação das Partes"
              desc="Qualificação completa do autor e réu (Art. 319, II, CPC)."
            />
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Qualificação do Autor{" "}
                {selectedClient && (
                  <span className="text-emerald-500">(preenchida do CRM)</span>
                )}
              </label>
              <textarea
                value={form.qualificacaoAutor}
                onChange={(e) =>
                  updateField("qualificacaoAutor", e.target.value)
                }
                rows={4}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Nome do Réu
              </label>
              <input
                value={form.nomeReu}
                onChange={(e) => updateField("nomeReu", e.target.value)}
                placeholder="Nome completo do réu"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Qualificação do Réu
              </label>
              <textarea
                value={form.qualificacaoReu}
                onChange={(e) => updateField("qualificacaoReu", e.target.value)}
                rows={4}
                placeholder="Pessoa jurídica de direito privado, inscrita no CNPJ sob o nº..."
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
              />
            </div>
          </div>
        )}

        {/* DOS FATOS */}
        {activeSection === "fatos" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Dos Fatos"
              desc="Narração dos fatos que fundamentam o pedido (Art. 319, III, CPC)."
            />
            <textarea
              value={form.fatos}
              onChange={(e) => updateField("fatos", e.target.value)}
              rows={16}
              placeholder="Descreva os fatos que originam a demanda judicial. Cada parágrafo será um bloco no documento final."
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y font-mono leading-relaxed"
            />
          </div>
        )}

        {/* DO DIREITO */}
        {activeSection === "fundamentos" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Do Direito"
              desc="Fundamentos jurídicos do pedido (Art. 319, III, CPC)."
            />
            <textarea
              value={form.fundamentos}
              onChange={(e) => updateField("fundamentos", e.target.value)}
              rows={16}
              placeholder="Exponha os dispositivos legais, jurisprudência e doutrina que sustentam o direito do autor."
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y font-mono leading-relaxed"
            />
          </div>
        )}

        {/* DOS PEDIDOS */}
        {activeSection === "pedidos" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Dos Pedidos"
              desc="Liste os pedidos com suas especificações (Art. 319, IV, CPC)."
            />
            {form.pedidos.map((pedido, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-[10px] font-bold text-[#20243B] mt-2.5 w-6 shrink-0">
                  {String.fromCharCode(97 + idx)})
                </span>
                <textarea
                  value={pedido}
                  onChange={(e) => handleUpdatePedido(idx, e.target.value)}
                  rows={2}
                  placeholder={`Pedido ${String.fromCharCode(97 + idx)})`}
                  className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
                />
                {form.pedidos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePedido(idx)}
                    className="mt-2 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddPedido}
              className="flex items-center gap-1.5 text-[10px] font-bold text-[#C5A059] hover:text-[#20243B] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Adicionar Pedido
            </button>
          </div>
        )}

        {/* COMPLEMENTO */}
        {activeSection === "complemento" && (
          <div className="space-y-4 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <SectionHeader
              title="Complemento"
              desc="Valor da causa, provas e audiência de conciliação (Art. 319, V-VII, CPC)."
            />
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Valor da Causa (R$)
              </label>
              <input
                value={form.valorCausa}
                onChange={(e) => updateField("valorCausa", e.target.value)}
                placeholder="Ex: 50.000,00"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1">
                Provas
              </label>
              <textarea
                value={form.provas}
                onChange={(e) => updateField("provas", e.target.value)}
                rows={4}
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                checked={form.audienciaConciliacao}
                onChange={(e) =>
                  updateField("audienciaConciliacao", e.target.checked)
                }
                className="accent-[#C5A059] w-4 h-4"
                id="audiencia-check"
              />
              <label
                htmlFor="audiencia-check"
                className="text-xs text-gray-700 cursor-pointer"
              >
                <span className="font-bold">Manifesta interesse</span> na
                audiência de conciliação ou mediação (Art. 319, VII, CPC)
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const rightPanelJsx = (
    <div className="hidden xl:flex w-80 2xl:w-96 bg-white border-l border-gray-200 h-full flex-col shadow-xl z-20 flex-shrink-0">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <Eye className="w-3 h-3" /> Prévia da Petição
        </h3>
        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">
          A4
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-gray-100">
        <div className="bg-white shadow-xl rounded-sm border border-gray-200 overflow-hidden">
          <iframe
            title="Petition Preview"
            srcDoc={previewHtml}
            className="w-full border-0"
            style={{ height: "100%", minHeight: "700px" }}
            sandbox="allow-same-origin"
          />
        </div>
      </div>
      <div className="p-2 bg-gray-50 text-[9px] text-center text-gray-400 border-t border-gray-200">
        Prévia em tempo real
      </div>
    </div>
  );

  return (
    <>
      {/* Notice toast */}
      {notice && (
        <div
          className={`fixed top-4 right-4 z-[1400] px-4 py-2 text-xs font-bold rounded-lg shadow-xl border ${notice.startsWith("✅") ? "bg-emerald-50 text-emerald-700 border-emerald-200" : notice.startsWith("❌") ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}
        >
          {notice}
        </div>
      )}

      {/* Publish result toast */}
      {publishResult && (
        <div
          className={`fixed top-16 right-4 z-[1400] px-4 py-3 rounded-lg shadow-xl border max-w-sm ${publishResult.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
        >
          <div className="flex items-start gap-2">
            {publishResult.success ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            )}
            <div>
              {publishResult.success ? (
                <>
                  <div className="text-xs font-bold text-emerald-800">
                    Petição publicada com sucesso!
                  </div>
                  <div className="text-[10px] text-emerald-600 mt-0.5">
                    ID: {publishResult.docId} · SHA-256:{" "}
                    {publishResult.hash
                      ? `${publishResult.hash.slice(0, 12)}...`
                      : "N/A"}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowConsentBuilder(true)}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#C5A059] bg-[#C5A059]/10 text-[10px] font-bold text-[#20243B] hover:bg-[#C5A059]/20 transition-colors"
                  >
                    <ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]" /> Gerar
                    Consentimento Informado
                  </button>
                </>
              ) : (
                <>
                  <div className="text-xs font-bold text-red-800">
                    Falha na publicação (SAGA rollback executado)
                  </div>
                  <div className="text-[10px] text-red-600 mt-0.5">
                    {publishResult.error}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DocumentShell
        title="Petition Engine"
        tabs={FORM_SECTIONS.map((s) => ({ id: s.id, label: s.label }))}
        activeTab={activeSection}
        onTabChange={setActiveSection}
        actionBar={actionBarJsx}
        centerPanel={centerPanelJsx}
        rightPanel={rightPanelJsx}
      >
        <div className="space-y-3 px-1">
          {/* METADADOS RESUMIDOS */}
          <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm space-y-2">
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
              Metadados
            </div>
            {selectedClient ? (
              <div className="flex items-center gap-2">
                <User className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-[#20243B] truncate">
                    {selectedClient.name ||
                      selectedClient.qualification?.physical?.name ||
                      "Cliente"}
                  </div>
                  <div className="text-[9px] text-gray-500">
                    {selectedClient.qualification?.physical?.cpf ||
                      selectedClient.qualification?.legal?.cnpj ||
                      ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClient(null);
                    updateField("qualificacaoAutor", "");
                  }}
                  className="text-[9px] text-red-500 font-bold hover:text-red-700"
                >
                  Trocar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setClientPickerOpen(true)}
                className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-[10px] text-gray-500 font-bold hover:border-[#C5A059] hover:text-[#20243B] transition-all flex items-center justify-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Selecionar Cliente
              </button>
            )}
            {selectedClient && (
              <CadernoSelector
                clientId={selectedClient.id}
                value={cadernoId}
                onChange={(id, title) => {
                  setCadernoId(id);
                  setCadernoTitle(title);
                }}
                label="Caderno"
              />
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="text-[10px]">
                <span className="text-gray-400 font-bold uppercase">Tipo:</span>
                <span className="ml-1 text-[#20243B]">{form.tipoPeticao}</span>
              </div>
              <div className="text-[10px]">
                <span className="text-gray-400 font-bold uppercase">Ação:</span>
                <span className="ml-1 text-[#20243B] truncate block">
                  {form.tipoAcao || "—"}
                </span>
              </div>
            </div>
            <div className="text-[10px]">
              <span className="text-gray-400 font-bold uppercase">Juízo:</span>
              <span className="ml-1 text-[#20243B]">{form.juizo || "—"}</span>
            </div>
            <div className="text-[10px]">
              <span className="text-gray-400 font-bold uppercase">
                Comarca:
              </span>
              <span className="ml-1 text-[#20243B]">{form.comarca}</span>
            </div>
          </div>

          {/* PROGRESSO */}
          <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Progresso
            </div>
            <div className="space-y-1">
              {FORM_SECTIONS.map((s) => {
                const hasContent = (() => {
                  if (s.id === "setup")
                    return !!selectedClient && !!form.tipoPeticao;
                  if (s.id === "enderecamento") return !!form.juizo.trim();
                  if (s.id === "partes")
                    return (
                      !!form.qualificacaoAutor.trim() && !!form.nomeReu.trim()
                    );
                  if (s.id === "fatos") return !!form.fatos.trim();
                  if (s.id === "fundamentos") return !!form.fundamentos.trim();
                  if (s.id === "pedidos")
                    return form.pedidos.some((p) => p.trim());
                  if (s.id === "complemento") return !!form.valorCausa.trim();
                  return false;
                })();
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${activeSection === s.id ? "bg-blue-50 text-[#20243B] font-bold border border-blue-200" : "hover:bg-gray-50 text-gray-600"}`}
                  >
                    {hasContent ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    ) : (
                      <div className="w-3 h-3 rounded-full border border-gray-300 shrink-0" />
                    )}
                    <span className="flex-1 text-left">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </DocumentShell>

      {/* CLIENT PICKER MODAL */}
      <CrmClientPickerModal
        isOpen={clientPickerOpen}
        onClose={() => setClientPickerOpen(false)}
        onSelect={handleClientSelect}
        db={db}
        appId={appId}
        title="Selecionar Cliente (Autor)"
      />

      {/* CONSENT BUILDER MODAL */}
      {showConsentBuilder && publishResult?.success && (
        <InformedConsentFactory
          petitionDocId={publishResult.docId}
          petitionTitle={publishResult.title || form.tipoAcao || "Petição"}
          clientId={selectedClient?.id || ""}
          clientName={
            selectedClient?.name ||
            selectedClient?.qualification?.physical?.name ||
            ""
          }
          cadernoId={cadernoId}
          cadernoTitle={cadernoTitle}
          onClose={() => setShowConsentBuilder(false)}
          onSuccess={() => {
            setShowConsentBuilder(false);
            pushNotice(
              "✅ Consentimento informado publicado no portal do cliente!",
            );
          }}
        />
      )}
    </>
  );
};

// --- Section Header ---
const SectionHeader = ({ title, desc }) => (
  <div className="pb-3 border-b border-gray-200">
    <h3 className="text-sm font-bold text-[#20243B]">{title}</h3>
    {desc && <p className="text-[10px] text-gray-500 mt-0.5">{desc}</p>}
  </div>
);

// --- FACTORY EXPORT (padrão CDAS) ---
export const PetitionFactory = () => <PetitionEngine />;
export default PetitionEngine;
